import type {
  DashboardField,
  DashboardFieldRole,
  DashboardWidget,
  DashboardWidgetType,
} from './types';
import { wellsOf } from './widgetTypes';
import type { DashboardWell } from './widgetTypes';

/**
 * What changing a widget's visualisation does to the fields already placed
 * (SL-1665, AC3).
 *
 * Pure and read-only: the panel calls it **before** `setWidgetType` to decide
 * whether the switch needs a confirmation, and to tell the user what it costs.
 * It describes; `setWidgetType` does. Both agree because both are built on the
 * same rule — a **role** is a well's `accepts`, `any` included — and on the same
 * parking slot (`parkedSlot`). They are not merely alike: both call
 * `applyTypeChange`, and the plan is what that function reports.
 *
 * `wells` holds only what the widget's type declares. Whatever the new type does
 * not read under the same key **and** role is parked, so it is never inert in
 * `wells` waiting to be misread by another type.
 */

/**
 * Why a configured well does not carry over to the new type. All three are
 * parked, not lost: they come back if the type does, until the dashboard is saved.
 */
export type DiscardReason = 'noWell' | 'roleMismatch' | 'overflow';

/** One well's worth of fields, named for the panel to list. */
export interface WellMove {
  wellKey: string;
  /** The i18n leaf that names the well — `labelKey ?? key` — in the type it is described in. */
  labelKey: string;
  fieldCount: number;
}

export interface DiscardedWell extends WellMove {
  reason: DiscardReason;
}

/** Fields of one well that the new type reads under another well (SL-1794). */
export interface MovedWell extends WellMove {
  /** The well they land in, in the new type. */
  toWellKey: string;
  /** The i18n leaf that names it — `labelKey ?? key`. */
  toLabelKey: string;
}

export interface TypeChangePlan {
  /** Wells whose fields the new type reads as they are. */
  kept: WellMove[];
  /** Wells whose fields the new type reads under another well. */
  moved: MovedWell[];
  /** Wells, or the part of one, the new type will not read. */
  discarded: DiscardedWell[];
  /** Wells parked by an earlier switch that the new type takes back. */
  restored: WellMove[];
  /** True when something is discarded: the only case worth interrupting for. */
  needsConfirm: boolean;
}

/** The `parkedWells` key of a well's fields under one role. */
export const parkedSlot = (wellKey: string, accepts: DashboardWell['accepts']): string =>
  `${wellKey}:${accepts}`;

/** What `applyTypeChange` produced: the plan's lists plus the resulting fields. */
export interface TypeChangeResult extends Omit<TypeChangePlan, 'needsConfirm'> {
  wells: DashboardWidget['wells'];
  /** The widget's `parkedWells` after the switch; may be empty. */
  parked: NonNullable<DashboardWidget['parkedWells']>;
}

const labelOf = (well: DashboardWell): string => well.labelKey ?? well.key;

/**
 * Parks `fields` under `slot` without overwriting what an earlier switch already
 * parked there: the two lists are merged and deduped by `columnId`, the fields
 * being parked **now** first. They are the ones that were in the well one hop
 * ago, so they sit before what was parked earlier — Bar [A, B, C] → Scatter
 * (parks [B, C]) → KPI (parks [A]) leaves [A, B, C], and going back to Bar
 * restores the original order.
 */
const park = (parked: TypeChangeResult['parked'], slot: string, fields: DashboardField[]): void => {
  const seen = new Set<string>();
  parked[slot] = [...fields, ...(parked[slot] ?? [])].filter((field) => {
    if (seen.has(field.columnId)) return false;
    seen.add(field.columnId);
    return true;
  });
};

export interface TypeChangeOptions {
  /**
   * Whether fields without a well under their own key move to a compatible one
   * (SL-1794). `false` for the assistant's proposal (`replaceWidget`): there the
   * proposal decides the wells, so a moved field would neither be in them nor
   * stay parked — it would just be gone.
   */
  move?: boolean;
}

/** Fields this switch parked for lack of a well under their own key. */
interface HomelessWell {
  previous: DashboardWell;
  fields: DashboardField[];
}

/**
 * A field as the chart reads it: one column of one table, under one
 * calculation. Two measures over the same column (sum and average) are two
 * fields.
 */
const identityOf = (field: DashboardField): string =>
  `${field.tableId ?? ''}::${field.columnId}::${field.calculation ?? ''}`;

/** A parked field, told apart by the slot it waits in. */
const parkedKey = (slot: string, field: DashboardField): string => `${slot}|${identityOf(field)}`;

/** A field's role: a measure carries its calculation, a dimension does not. */
const roleOf = (field: DashboardField): DashboardFieldRole =>
  field.calculation ? 'measure' : 'dimension';

/** Drops one field from a parked slot, and the slot when it empties. */
const unpark = (parked: TypeChangeResult['parked'], slot: string, field: DashboardField): void => {
  const left = (parked[slot] ?? []).filter((other) => identityOf(other) !== identityOf(field));
  if (left.length > 0) parked[slot] = left;
  else delete parked[slot];
};

/**
 * Where moved and refilled fields land, and in what order (SL-1794).
 *
 * A well can hold a field when it takes the field's role (or any), is not
 * numeric-only (the plan does not know the columns' types) and does not merely
 * decorate the chart (`adoptsMoved`). It must be empty, or take several fields
 * and have been filled by this same switch, so nothing the user put there is
 * displaced.
 *
 * `wells` (and the `parked` the passes below edit) are the shallow copies
 * `applyTypeChange` made for this switch: writing to them never reaches the
 * widget they came from.
 *
 * Into a well the restore just filled, a field goes **before** what came back —
 * the rule `park` already follows: it left the well more recently than the
 * parked ones did, so it sat ahead of them. Bar [A, B, C] → Scatter → KPI → Bar
 * returns A, B, C, not B, C, A. Anywhere else it is appended.
 */
const createPlacer = (
  wells: DashboardWidget['wells'],
  nextWells: DashboardWell[],
  restoredWells: ReadonlySet<string>,
) => {
  const filledHere = new Set(restoredWells);
  const placed = new Set(Object.values(wells).flat().map(identityOf));
  const ahead = new Map<string, number>();

  const canHold = (well: DashboardWell, field: DashboardField): boolean => {
    if (well.adoptsMoved === false || well.numericOnly) return false;
    if (well.accepts !== 'any' && well.accepts !== roleOf(field)) return false;
    const held = wells[well.key] ?? [];
    return held.length === 0 || (well.multiple && filledHere.has(well.key));
  };

  return {
    wellFor: (field: DashboardField) => nextWells.find((well) => canHold(well, field)),
    requiredWellFor: (field: DashboardField) =>
      nextWells.find((well) => well.required && canHold(well, field)),
    isPlaced: (field: DashboardField) => placed.has(identityOf(field)),
    place: (well: DashboardWell, field: DashboardField) => {
      const current = wells[well.key] ?? [];
      if (restoredWells.has(well.key)) {
        const at = ahead.get(well.key) ?? 0;
        wells[well.key] = [...current.slice(0, at), field, ...current.slice(at)];
        ahead.set(well.key, at + 1);
      } else {
        wells[well.key] = [...current, field];
      }
      filledHere.add(well.key);
      placed.add(identityOf(field));
    },
  };
};

type Placer = ReturnType<typeof createPlacer>;

/**
 * The plan's discarded entry for `wellKey`, lighter by `count` fields that
 * moved after all — and gone when none is left — so the plan only lists as lost
 * what really is. Overflow entries are a different loss and are left alone.
 */
const shrinkDiscarded = (
  discarded: DiscardedWell[],
  wellKey: string,
  count: number,
): DiscardedWell[] => {
  const index = discarded.findIndex(
    (entry) => entry.wellKey === wellKey && entry.reason !== 'overflow',
  );
  if (index < 0) return discarded;
  const remaining = discarded[index].fieldCount - count;
  return remaining > 0
    ? discarded.map((entry, at) => (at === index ? { ...entry, fieldCount: remaining } : entry))
    : discarded.filter((_, at) => at !== index);
};

/**
 * Moves what this switch could not keep, in the old type's well order, to a
 * well that can hold it (SL-1794). A moved field leaves its parked slot — no
 * copy stays behind: the draft is compared whole to say whether there is
 * something to save, so a copy would mark a dashboard that came back to where
 * it was as changed.
 *
 * The same column in two wells is two uses of it, and each moves on its own
 * (Bar with Status as X axis and legend → KPI → Bar comes back with both).
 */
const moveHomeless = (
  homeless: HomelessWell[],
  placer: Placer,
  parked: TypeChangeResult['parked'],
): MovedWell[] =>
  homeless.flatMap(({ previous, fields }) => {
    const slot = parkedSlot(previous.key, previous.accepts);
    const landed = new Map<DashboardWell, number>();
    for (const field of fields) {
      const target = placer.wellFor(field);
      if (!target) continue;
      placer.place(target, field);
      unpark(parked, slot, field);
      landed.set(target, (landed.get(target) ?? 0) + 1);
    }
    return [...landed].map(([to, fieldCount]) => ({
      wellKey: previous.key,
      labelKey: labelOf(previous),
      fieldCount,
      toWellKey: to.key,
      toLabelKey: labelOf(to),
    }));
  });

/**
 * Fills a required well still empty with a compatible field parked by an
 * **earlier** switch, so Bar → KPI → Scatter → Bar gets its Y axis back even
 * though no well of that name existed on the way. Only fields parked before
 * this switch qualify: one this switch just parked is already in the plan as
 * discarded, and refilling it would show the same field as lost and as back.
 * A field the widget already shows is skipped.
 */
const fillRequiredFromParked = (
  parked: TypeChangeResult['parked'],
  parkedBefore: ReadonlySet<string>,
  placer: Placer,
): WellMove[] => {
  const filled = new Map<DashboardWell, number>();
  for (const [slot, fields] of Object.entries(parked)) {
    for (const field of fields) {
      if (!parkedBefore.has(parkedKey(slot, field)) || placer.isPlaced(field)) continue;
      const target = placer.requiredWellFor(field);
      if (!target) continue;
      placer.place(target, field);
      unpark(parked, slot, field);
      filled.set(target, (filled.get(target) ?? 0) + 1);
    }
  }
  return [...filled].map(([well, fieldCount]) => ({
    wellKey: well.key,
    labelKey: labelOf(well),
    fieldCount,
  }));
};

/**
 * Applies the switch from the widget's current type to `nextType` and reports
 * what it did. The single implementation behind `setWidgetType` and
 * `planTypeChange`.
 *
 * - A well the new type reads under the same key and role is **kept**.
 * - Every field of any other well — the key is undeclared (`noWell`) or read
 *   under another role (`roleMismatch`) — is **parked** under
 *   `parkedSlot(key, previousRole)`. A slot an earlier switch already filled is
 *   merged into, never replaced (see `park`). Bar → KPI → Scatter therefore does not leave
 *   bar's dimension in `wells.xAxis` for the scatter to read as a measure.
 * - A single-valued well that receives several fields keeps the first; the
 *   rest (`overflow`) are parked under the well's own slot, so going back to a
 *   multiple well (Table → Matrix → Table) returns them.
 * - A parked slot the new type declares is **restored** into an empty well, so
 *   it never overwrites what the user has put there since. The one exception is
 *   the overflow return: into a multiple well that already holds fields, the
 *   parked ones not yet present are appended.
 *
 * An untyped widget, or the type it already has, changes nothing.
 */
export function applyTypeChange(
  widget: DashboardWidget,
  nextType: DashboardWidgetType,
  { move = true }: TypeChangeOptions = {},
): TypeChangeResult {
  const kept: WellMove[] = [];
  let discarded: DiscardedWell[] = [];
  const restored: WellMove[] = [];
  const wells = { ...widget.wells };
  const parked = { ...widget.parkedWells };
  // What this switch parked for lack of a well under the same key, in the order
  // of the old type's wells: the candidates for `moveHomeless`.
  const homeless: HomelessWell[] = [];
  // Wells the restore below filled: a moved field may join them when they take
  // several fields, since nothing there is the user's doing since the switch.
  const restoredHere = new Set<string>();

  if (!widget.type || widget.type === nextType) {
    return { wells, parked, kept, moved: [], discarded, restored };
  }

  // Before this switch parks anything, slot by slot: only these may refill a
  // required well. Per slot, because the same column can be parked again now.
  const parkedBefore = new Set(
    Object.entries(parked).flatMap(([slot, fields]) =>
      fields.map((field) => parkedKey(slot, field)),
    ),
  );

  const nextWells = wellsOf(nextType);

  for (const previous of wellsOf(widget.type)) {
    const fields = wells[previous.key];
    if (!fields?.length) continue;

    const move = { wellKey: previous.key, labelKey: labelOf(previous), fieldCount: fields.length };
    const next = nextWells.find((candidate) => candidate.key === previous.key);
    const slot = parkedSlot(previous.key, previous.accepts);

    if (!next || next.accepts !== previous.accepts) {
      discarded.push({ ...move, reason: next ? 'roleMismatch' : 'noWell' });
      park(parked, slot, fields);
      delete wells[previous.key];
      homeless.push({ previous, fields });
    } else if (!next.multiple && fields.length > 1) {
      kept.push({ ...move, fieldCount: 1 });
      discarded.push({ ...move, fieldCount: fields.length - 1, reason: 'overflow' });
      wells[previous.key] = fields.slice(0, 1);
      park(parked, slot, fields.slice(1));
    } else {
      kept.push(move);
    }
  }

  for (const next of nextWells) {
    const slot = parkedSlot(next.key, next.accepts);
    const back = parked[slot];
    if (!back?.length) continue;

    const current = wells[next.key] ?? [];
    let returning = back;
    if (current.length > 0) {
      if (!next.multiple) continue;
      const present = new Set(current.map((field) => field.columnId));
      returning = back.filter((field) => !present.has(field.columnId));
    }

    delete parked[slot];
    if (returning.length === 0) continue;

    wells[next.key] = [...current, ...returning];
    restoredHere.add(next.key);
    restored.push({ wellKey: next.key, labelKey: labelOf(next), fieldCount: returning.length });
  }

  if (!move) return { wells, parked, kept, moved: [], discarded, restored };

  const placer = createPlacer(wells, nextWells, restoredHere);
  const moved = moveHomeless(homeless, placer, parked);
  for (const entry of moved)
    discarded = shrinkDiscarded(discarded, entry.wellKey, entry.fieldCount);
  for (const entry of fillRequiredFromParked(parked, parkedBefore, placer)) {
    const same = restored.find((back) => back.wellKey === entry.wellKey);
    if (same) same.fieldCount += entry.fieldCount;
    else restored.push(entry);
  }

  return { wells, parked, kept, moved, discarded, restored };
}

/**
 * Describes the switch to `nextType` without doing it: what `applyTypeChange`
 * would keep, park and bring back.
 */
export function planTypeChange(
  widget: DashboardWidget,
  nextType: DashboardWidgetType,
): TypeChangePlan {
  const { kept, moved, discarded, restored } = applyTypeChange(widget, nextType);
  return { kept, moved, discarded, restored, needsConfirm: discarded.length > 0 };
}
